import Dexie, { type EntityTable } from "dexie";

export type VariantElement = {
  id: string;
  elementId: string;
  personalisedContent: string | null;
  persistedPersonalisedContent: string;
  originalContent: string;
  variantId: string;
  updatedAt: string;
  selector: string;
};

export class VariantContentStore {
  private db = new Dexie("VariantContentDatabase") as Dexie & {
    variantElements: EntityTable<VariantElement, "id">;
  };

  private constructor() {
    this.db.version(2).stores({
      variantElements:
        "++id, elementId, variantId, [elementId+variantId], personalisedContent, persistedPersonalisedContent, originalContent, updatedAt, selector",
    });
  }

  static async init(
    variantId: string,
    savedElements: {
      elementId: string;
      personalisedContent: string | null;
      originalContent: string;
      selector: string;
      updatedAt: Date;
    }[],
  ): Promise<VariantContentStore> {
    const instance = new VariantContentStore();
    await instance.syncElements(
      variantId,
      savedElements.map((e) => ({
        ...e,
        updatedAt: e.updatedAt.toISOString(),
      })),
    );
    return instance;
  }

  private async syncElements(
    variantId: string,
    savedElements: {
      elementId: string;
      personalisedContent: string | null;
      originalContent: string;
      selector: string;
      updatedAt: string;
    }[],
  ) {
    const cachedElements = await this.getElements(variantId);
    const elementsToUpdate = cachedElements.filter(
      (element) => !savedElements.includes(element),
    );
    const elementsToCreate = savedElements.filter(
      (element) =>
        !cachedElements.some((e) => e.elementId === element.elementId),
    );

    await this.db.transaction("rw", this.db.variantElements, async () => {
      elementsToUpdate.forEach(async (cachedElement) => {
        const savedElement = savedElements.find(
          (e) => e.elementId === cachedElement.elementId,
        )!;

        if (
          new Date(savedElement.updatedAt) > new Date(cachedElement.updatedAt)
        ) {
          await this.db.variantElements
            .where(["elementId", "variantId"])
            .equals([savedElement.elementId, variantId])
            .modify({
              personalisedContent: savedElement.personalisedContent,
              originalContent: savedElement.originalContent,
              updatedAt: savedElement.updatedAt,
              selector: savedElement.selector,
            });
        }
      });

      await this.db.variantElements.bulkPut(
        elementsToCreate.map((element) => ({
          personalisedContent: element.personalisedContent,
          persistedPersonalisedContent:
            element.personalisedContent ?? element.originalContent,
          originalContent: element.originalContent,
          elementId: element.elementId,
          variantId,
          updatedAt: element.updatedAt,
          selector: element.selector,
        })),
      );
    });
  }

  async updatePersonalisedContents(
    variantId: string,
    elements: {
      elementId: string;
      personalisedContent: string | null;
    }[],
  ) {
    await this.db.transaction("rw", this.db.variantElements, async () => {
      await Promise.all(
        elements.map(async (element) => {
          this.db.variantElements
            .where(["elementId", "variantId"])
            .equals([element.elementId, variantId])
            .modify({
              personalisedContent: element.personalisedContent,
              updatedAt: new Date().toISOString(),
            });
        }),
      );
    });
  }

  async getElements(variantId: string) {
    return this.db.variantElements
      .where("variantId")
      .equals(variantId)
      .toArray();
  }

  async getElement(variantId: string, elementId: string) {
    return this.db.variantElements
      .where(["elementId", "variantId"])
      .equals([elementId, variantId])
      .first();
  }

  async discardAll(variantId: string) {
    await this.db.transaction("rw", this.db.variantElements, async () => {
      const curr = await this.db.variantElements
        .where("variantId")
        .equals(variantId)
        .toArray();

      await Promise.all(
        curr.map(async (element) => {
          this.db.variantElements
            .where(["elementId", "variantId"])
            .equals([element.elementId, variantId])
            .modify({
              personalisedContent: element.persistedPersonalisedContent,
            });
        }),
      );
    });
  }

  async syncOnSave(
    variantId: string,
    elements: {
      elementId: string;
      savedPersonalisedContent: string;
    }[],
  ) {
    await this.db.transaction("rw", this.db.variantElements, async () => {
      const curr = await this.db.variantElements
        .where("variantId")
        .equals(variantId)
        .toArray();

      await Promise.all(
        curr.map(async (element) => {
          const savedElement = elements.find(
            (e) => e.elementId === element.elementId,
          )!;

          await this.db.variantElements
            .where(["elementId", "variantId"])
            .equals([savedElement.elementId, variantId])
            .modify({
              persistedPersonalisedContent:
                savedElement.savedPersonalisedContent,
              personalisedContent: savedElement.savedPersonalisedContent,
            });
        }),
      );
    });
  }
}
